import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

// Dynamic import for GLTFLoader to avoid bundler issues
let GLTFLoaderClass: any = null;
async function getGLTFLoader() {
  if (!GLTFLoaderClass) {
    const module = await import('three/examples/jsm/loaders/GLTFLoader.js');
    GLTFLoaderClass = module.GLTFLoader;
  }
  return GLTFLoaderClass;
}

interface Model3DPreviewProps {
  modelUrl: string;
  autoRotate?: boolean;
}

export const Model3DPreview: React.FC<Model3DPreviewProps> = ({
  modelUrl,
  autoRotate = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const modelRef = useRef<THREE.Group | null>(null);
  const animationFrameRef = useRef<number>(0);

  useEffect(() => {
    if (!containerRef.current) return;

    // Initialize Three.js scene
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      45,
      width / height,
      0.1,
      1000
    );
    camera.position.set(0, 0, 5);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true,
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 5, 5);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      if (autoRotate && modelRef.current) {
        modelRef.current.rotation.y += 0.005;
      }

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;

      const newWidth = containerRef.current.clientWidth;
      const newHeight = containerRef.current.clientHeight;

      cameraRef.current.aspect = newWidth / newHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(newWidth, newHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameRef.current);
      
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      
      renderer.dispose();
    };
  }, [autoRotate]);

  // Load model
  useEffect(() => {
    if (!modelUrl || !sceneRef.current || !rendererRef.current) return;

    setIsLoading(true);
    setError(null);

    // Remove previous model
    if (modelRef.current) {
      sceneRef.current.remove(modelRef.current);
      modelRef.current = null;
    }

    // Load GLB model using dynamic import
    const loadModel = async () => {
      try {
        const GLTFLoader = await getGLTFLoader();
        const loader = new GLTFLoader();
        
        loader.load(
          modelUrl,
          (gltf: any) => {
            const model = gltf.scene;
            
            // Center and scale model
            const box = new THREE.Box3().setFromObject(model);
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());
            
            // Center model
            model.position.x = -center.x;
            model.position.y = -center.y;
            model.position.z = -center.z;
            
            // Scale to fit view
            const maxDim = Math.max(size.x, size.y, size.z);
            const scale = 2 / maxDim;
            model.scale.setScalar(scale);
            
            // Enable shadows
            model.traverse((child: any) => {
              if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
                
                if (child.material) {
                  child.material.needsUpdate = true;
                }
              }
            });
            
            sceneRef.current?.add(model);
            modelRef.current = model;
            setIsLoading(false);
          },
          (progress: any) => {
            if (progress.total > 0) {
              const percent = (progress.loaded / progress.total) * 100;
              console.log(`Loading: ${percent.toFixed(0)}%`);
            }
          },
          (err: any) => {
            console.error('Failed to load model:', err);
            setError('Failed to load 3D model. Please try again.');
            setIsLoading(false);
          }
        );
      } catch (err) {
        console.error('Failed to initialize GLTFLoader:', err);
        setError('Failed to load 3D model loader.');
        setIsLoading(false);
      }
    };

    loadModel();
  }, [modelUrl]);

  return (
    <div style={{ width: '100%' }}>
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '400px',
          backgroundColor: 'var(--tl4k-surface-warm)',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.8)',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
              <div style={{ fontSize: '14px', color: 'var(--tl4k-neutral-600)' }}>Loading 3D model...</div>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.9)',
            }}
          >
            <div
              style={{
                textAlign: 'center',
                padding: '20px',
                backgroundColor: 'var(--tl4k-surface-warm)',
                borderRadius: '8px',
                border: '1px solid #fcc',
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>❌</div>
              <div style={{ fontSize: '14px', color: 'var(--tl4k-error-700)' }}>{error}</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--tl4k-neutral-600)', textAlign: 'center' }}>
        🖱️ Drag to rotate • Scroll to zoom
      </div>
    </div>
  );
};

export default Model3DPreview;
